import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { createMRTColumnHelper, type MRT_TableOptions } from "material-react-table";
import { useCallback, useMemo } from "react";
import { useSession } from "users/__session/session-context";
import UserInformation from "users/information/user-information";
import { useGetUserNetworkConnectionsQuery } from "users/networking/connections/_api/user-network-connection-api";

const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

type NetworkedIndividualsProps = {
  selectedMemberUserIds?: Guid[];
  onSelected: (rowData: UserInformation) => void;
};

const columnHelper = createMRTColumnHelper<UserInformation>();

export default function NetworkedIndividuals(props: Readonly<NetworkedIndividualsProps>) {
  const {
    selectedMemberUserIds,
    onSelected
  } = props;

  const session = useSession();

  const networkConnectionQuery = useGetUserNetworkConnectionsQuery();

  const networkConnections = useMemo(() => {
    return UserInformation.fromJSONArray(networkConnectionQuery.data ?? [])
  }, [networkConnectionQuery.data]);
  
  const renderNameColumn = useCallback((networkConnection: UserInformation) => {
    if (!networkConnection.name) return <Icon>remove</Icon>;
    return (
      <NameCellContainer>
        <IndividualAvatar
          individual={networkConnection.toIndividual()}
          session={session}
        />
        <RowTitle>{networkConnection.name.fullName}</RowTitle>
      </NameCellContainer>
    );
  }, [session]);

  const renderEmailColumn = useCallback((networkConnection: UserInformation) => {
    if (!networkConnection.email) return <Icon>remove</Icon>;
    const email = networkConnection.email.toString();
    return <Link href={`mailto:${email}`}>{email}</Link>;
  }, []);
  
  const columns = useMemo(() => [
    columnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
    }),
    columnHelper.accessor(renderEmailColumn, {
      header: "Email",
      id: "email",
    }),
  ], [renderNameColumn, renderEmailColumn]);

  const tableOptions: MRT_TableOptions<UserInformation> = {
    columns,
    data: networkConnections ?? [],
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => {
      const disabled = selectedMemberUserIds?.some(
        (id) => row.original?.id && id.isEqualTo(row.original.id)
      );
      return {
        onClick: disabled ? undefined : () => onSelected(row.original),
        sx: {
          pointerEvents: disabled ? "none" : "auto",
          cursor: "pointer",
        },
      };
    },
    enableColumnPinning: true,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiToolbarAlertBannerProps: networkConnectionQuery.isError
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    // onColumnFiltersChange: setColumnFilters,
    // onGlobalFilterChange: setGlobalFilter,
    // onPaginationChange: setPagination,
    // onSortingChange: setSorting,
    rowCount: networkConnections?.length ?? 0,
    state: {
      // columnFilters,
      // globalFilter,
      isLoading: networkConnectionQuery.isLoading,
      // pagination,
      showAlertBanner: Boolean(networkConnectionQuery.isError),
      showProgressBars: networkConnectionQuery.isLoading,
      // sorting,
    },
  }

  return <MRTNoMemo<UserInformation> options={tableOptions} />;
}
